import React, { useState, useEffect } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@material-ui/core'
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon
} from '@material-ui/icons'
import { tourApi } from '../api/tourApi'
import moment from 'moment'

/**
 * Tour history component
 * Shows timeline of events and status changes
 */
const TourHistory = () => {
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [eventFilter, setEventFilter] = useState('all')
  const [dateFilter, setDateFilter] = useState('all')

  useEffect(() => {
    loadHistory()
  }, [])

  const loadHistory = async () => {
    setLoading(true)
    setError(null)
    
    try {
      // Simulate history data (in production would come from API)
      const mockHistory = generateMockHistory()
      setHistory(mockHistory)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const generateMockHistory = () => {
    const events = [
      { type: 'created', label: 'Tour created' },
      { type: 'processed', label: 'Tour processed' },
      { type: 'cancelled', label: 'Tour cancelled' },
      { type: 'modified', label: 'Tour modified' },
      { type: 'payment', label: 'Payment received' },
      { type: 'email', label: 'Email sent' }
    ]

    return Array.from({ length: 20 }, (_, index) => ({
      id: index + 1,
      tourId: Math.floor(Math.random() * 1000) + 1,
      event: events[Math.floor(Math.random() * events.length)],
      timestamp: moment().subtract(Math.floor(Math.random() * 30), 'days').toDate(),
      user: `User ${Math.floor(Math.random() * 10) + 1}`,
      details: `Event details ${index + 1}`,
      status: ['success', 'error', 'warning', 'info'][Math.floor(Math.random() * 4)]
    }))
  }

  const filteredHistory = history.filter(item => {
    const matchesSearch = searchTerm === '' || 
      item.tourId.toString().includes(searchTerm) ||
      item.event.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.user.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesEvent = eventFilter === 'all' || item.event.type === eventFilter

    const matchesDate = dateFilter === 'all' || 
      (dateFilter === 'today' && moment(item.timestamp).isSame(moment(), 'day')) ||
      (dateFilter === 'week' && moment(item.timestamp).isAfter(moment().subtract(7, 'days'))) ||
      (dateFilter === 'month' && moment(item.timestamp).isAfter(moment().subtract(30, 'days')))

    return matchesSearch && matchesEvent && matchesDate
  })

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'created': return <InfoIcon />
      case 'processed': return <CheckIcon />
      case 'cancelled': return <ErrorIcon />
      case 'modified': return <WarningIcon />
      case 'payment': return <CheckIcon />
      case 'email': return <InfoIcon />
      default: return <InfoIcon />
    }
  }

  const getEventColor = (eventType) => {
    switch (eventType) {
      case 'created': return 'primary'
      case 'processed': return 'success'
      case 'cancelled': return 'error'
      case 'modified': return 'warning'
      case 'payment': return 'success'
      case 'email': return 'info'
      default: return 'default'
    }
  }

  if (loading) {
    return <HistorySkeleton />
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">
            Error loading history: {error}
          </Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <div>
      {/* Filters */}
      <Card variant="outlined" style={{ marginBottom: 16 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search history..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Event type</InputLabel>
                <Select
                  value={eventFilter}
                  onChange={(e) => setEventFilter(e.target.value)}
                >
                  <MenuItem value="all">All events</MenuItem>
                  <MenuItem value="created">Created</MenuItem>
                  <MenuItem value="processed">Processed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="modified">Modified</MenuItem>
                  <MenuItem value="payment">Payments</MenuItem>
                  <MenuItem value="email">Emails</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Period</InputLabel>
                <Select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <MenuItem value="all">All period</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">Last week</MenuItem>
                  <MenuItem value="month">Last month</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadHistory}
              >
                Update
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Timeline of history */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Tour History
          </Typography>
          
          {filteredHistory.length > 0 ? (
            <div>
              {filteredHistory.map((item, index) => (
                <div key={item.id} style={{ marginBottom: 16 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={3}>
                      <Typography variant="body2" color="textSecondary">
                        {moment(item.timestamp).format('DD/MM/YYYY HH:mm')}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={1} style={{ textAlign: 'center' }}>
                      <div style={{ 
                        width: 40, 
                        height: 40, 
                        borderRadius: '50%', 
                        backgroundColor: getEventColor(item.event.type) === 'primary' ? '#1976d2' :
                                       getEventColor(item.event.type) === 'success' ? '#4caf50' :
                                       getEventColor(item.event.type) === 'error' ? '#f44336' :
                                       getEventColor(item.event.type) === 'warning' ? '#ff9800' : '#757575',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                      }}>
                        {getEventIcon(item.event.type)}
                      </div>
                    </Grid>
                    
                    <Grid item xs={8}>
                      <Card variant="outlined">
                        <CardContent>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <Typography variant="h6">
                              Tour #{item.tourId}
                            </Typography>
                            <Chip
                              label={item.event.label}
                              color={getEventColor(item.event.type)}
                              size="small"
                            />
                          </div>
                          
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            {item.details}
                          </Typography>
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption">
                              By: {item.user}
                            </Typography>
                            
                            <div>
                              <Tooltip title="View details">
                                <IconButton size="small">
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No events found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Try adjusting search filters
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * Loading component for history
 */
const HistorySkeleton = () => (
  <div>
    <Card variant="outlined" style={{ marginBottom: 16 }}>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <div style={{ height: 40, backgroundColor: '#f5f5f5', borderRadius: 4 }} />
          </Grid>
          <Grid item xs={12} md={3}>
            <div style={{ height: 40, backgroundColor: '#f5f5f5', borderRadius: 4 }} />
          </Grid>
          <Grid item xs={12} md={3}>
            <div style={{ height: 40, backgroundColor: '#f5f5f5', borderRadius: 4 }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <div style={{ height: 40, backgroundColor: '#f5f5f5', borderRadius: 4 }} />
          </Grid>
        </Grid>
      </CardContent>
    </Card>

    <Card>
      <CardContent style={{ textAlign: 'center', padding: 40 }}>
        <CircularProgress size={60} />
        <Typography variant="h6" style={{ marginTop: 16 }}>
          Loading history...
        </Typography>
      </CardContent>
    </Card>
  </div>
)

export default TourHistory 


// WEBPACK FOOTER //
// ./src/features/tours/components/TourHistory.js